import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { functions } from './firebase';
import { ReviewRequest } from '../types';

const REVIEWS_COLLECTION = 'projectReviews';
const LEGACY_SUBMITTED_STATUS = 'submitted';
const OPEN_REVIEW_STATUSES = new Set<ReviewRequest['status']>([
  'waiting_for_review',
  'in_review',
]);

function normalizeReviewStatus(status: string | undefined): ReviewRequest['status'] {
  if (status === LEGACY_SUBMITTED_STATUS || !status) {
    return 'waiting_for_review';
  }

  return status as ReviewRequest['status'];
}

function mapReviewRequest(id: string, data: Record<string, any>): ReviewRequest {
  return {
    id,
    projectId: data.projectId,
    ownerId: data.ownerId,
    projectTitle: data.projectTitle,
    status: normalizeReviewStatus(data.status),
    submitterId: data.submitterId,
    submitterName: data.submitterName || 'Unknown',
    submitterNotes: data.submitterNotes || '',
    adminNotes: data.adminNotes || '',
    createdAt: data.createdAt?.toDate?.() ?? new Date(),
    updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
    reviewedAt: data.reviewedAt?.toDate?.() ?? null,
    reviewedBy: data.reviewedBy ?? null,
    reviewedByName: data.reviewedByName ?? null,
    hasPreviousApproval: Boolean(data.hasPreviousApproval),
    previousReviewCount: typeof data.previousReviewCount === 'number'
      ? data.previousReviewCount
      : 0,
  };
}

export async function createReviewRequest(input: {
  projectId: string;
  ownerId: string;
  projectTitle: string;
  submitterId: string;
  submitterName: string;
  submitterEmail?: string | null;
  submitterNotes?: string;
}): Promise<string> {
  const fn = httpsCallable<
    { projectId: string; submitterNotes?: string },
    { reviewId: string }
  >(functions, 'submitProjectReview');
  const result = await fn({
    projectId: input.projectId,
    submitterNotes: input.submitterNotes,
  });
  return result.data.reviewId;
}

export async function getProjectReviewRequests(projectId: string): Promise<ReviewRequest[]> {
  const snapshot = await getDocs(
    query(collection(db, REVIEWS_COLLECTION), where('projectId', '==', projectId))
  );
  return snapshot.docs
    .map((doc) => mapReviewRequest(doc.id, doc.data()))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export async function getLatestOpenReviewRequest(projectId: string): Promise<ReviewRequest | null> {
  const reviews = await getProjectReviewRequests(projectId);
  return reviews.find((review) => OPEN_REVIEW_STATUSES.has(review.status)) || null;
}
